// Tiny composable that surfaces whether THIS device currently has an
// active phone-as-remote session paired with a TV elsewhere in the
// household. Used by App.vue to decide whether to render the header
// "open remote" shortcut icon — so a phone that drifted off into other
// app pages can return to /remote with one tap.
//
// We don't poll continuously. The probe runs:
//   - once on mount
//   - on every `visibilitychange → visible` (covers waking the phone)
//   - on every `online` (covers Wi-Fi handoff)
//
// Each probe is a single GET /api/remote/active call (cheap, server
// already serves this for RemoteHomeView), so the network cost is
// bounded by the user actually using the app.

import { ref, onMounted, onUnmounted } from "vue";
import { apiFetch, ApiError } from "@/services/apiClient";
import { useAuthStore } from "@/stores/auth";

export function useRemoteShortcut() {
  const hasActiveSession = ref(false);
  const sessionId = ref("");
  const authStore = useAuthStore();

  async function refresh() {
    // Skip the probe entirely when the invite doesn't carry the
    // `remote-control` permission — the endpoint will 403 anyway and
    // every poll surfaces as a red console error. The icon stays
    // hidden via the same auth-store check in the header template,
    // so there's nothing to refresh.
    if (!authStore.hasPermission("remote-control")) {
      hasActiveSession.value = false;
      sessionId.value = "";
      return;
    }
    try {
      const data = await apiFetch("/api/remote/active");
      if (data?.active && data.sessionId) {
        hasActiveSession.value = true;
        sessionId.value = String(data.sessionId);
      } else {
        hasActiveSession.value = false;
        sessionId.value = "";
      }
    } catch (err) {
      // 404 = "no active session" is the normal idle path. 401/403 =
      // user isn't unlocked — apiFetch already redirects on those, so
      // we just clear the flag and let the auth gate take over.
      if (err instanceof ApiError && err.status === 404) {
        hasActiveSession.value = false;
        sessionId.value = "";
      } else {
        // Network blip — keep prior state so the icon doesn't flicker
        // through a brief offline window.
      }
    }
  }

  function onVisibilityChange() {
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "visible"
    ) {
      refresh();
    }
  }

  onMounted(() => {
    refresh();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", refresh);
    }
  });

  onUnmounted(() => {
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("online", refresh);
    }
  });

  return {
    hasActiveSession,
    sessionId,
    refresh,
  };
}
